import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { API } from "@/context/AuthContext";

const RANGES = [
  { key: "today", label: "Heute" },
  { key: "7d", label: "Letzte 7 Tage" },
  { key: "all", label: "Alle" },
];

const fmt = (iso) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const parseUA = (ua) => {
  if (!ua) return "—";
  let browser = "Unbekannt";
  if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Edg\//.test(ua)) browser = "Edge";
  else if (/OPR\//.test(ua)) browser = "Opera";
  else if (/Chrome\//.test(ua)) browser = "Chrome";
  else if (/Safari\//.test(ua)) browser = "Safari";
  let os = "";
  if (/Windows NT 10/.test(ua)) os = "Win 10/11";
  else if (/Windows/.test(ua)) os = "Windows";
  else if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/iPhone|iPad|iOS/.test(ua)) os = "iOS";
  else if (/Linux/.test(ua)) os = "Linux";
  return os ? `${browser} · ${os}` : browser;
};

export default function WebsiteTracking() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [range, setRange] = useState("7d");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: logsData }, { data: sum }] = await Promise.all([
        axios.get(`${API}/tracking/logs`, { params: { range }, withCredentials: true }),
        axios.get(`${API}/tracking/summary`, { withCredentials: true }),
      ]);
      setLogs(logsData);
      setSummary(sum);
    } catch {
      toast.error("Tracking-Daten konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => summary || { today: 0, week: 0, month: 0, year: 0, total: 0 }, [summary]);

  return (
    <div data-testid="tracking-view">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {[["Heute", stats.today], ["7 Tage", stats.week], ["Monat", stats.month], [`Jahr`, stats.year], ["Gesamt", stats.total]].map(([label, val]) => (
          <div key={label} className="border border-[#E3E8EC] rounded-xl bg-white px-5 py-4">
            <div className="font-tech text-[10px] tracking-[0.2em] uppercase text-[var(--ci-grey)]">{label}</div>
            <div className="text-2xl font-bold text-[var(--ci-grey)] mt-1">{val}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center border border-[#E3E8EC] rounded-full overflow-hidden" data-testid="tracking-range-tabs">
          {RANGES.map((r) => (
            <button key={r.key} onClick={() => setRange(r.key)} data-testid={`tracking-range-${r.key}`}
              className={`px-4 h-10 font-tech text-[10px] tracking-[0.2em] uppercase transition-colors duration-200 ${range === r.key ? "bg-[var(--ci-blue)] text-white" : "text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)]"}`}>
              {r.label}
            </button>
          ))}
        </div>
        <button onClick={load} data-testid="tracking-refresh" className="flex items-center gap-2 px-4 h-10 rounded-full border border-[#E3E8EC] text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)] hover:border-[var(--ci-turquoise)] transition-colors duration-200 font-tech text-[11px] tracking-[0.18em] uppercase">
          <RefreshCw size={13} /> Aktualisieren
        </button>
      </div>

      <div className="border border-[#E3E8EC] rounded-2xl overflow-hidden bg-white" data-testid="tracking-table">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-[#F7F9FA] border-b border-[#E3E8EC]">
          <span className="col-span-3 font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">Datum / Uhrzeit</span>
          <span className="col-span-2 font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">IP (anonymisiert)</span>
          <span className="col-span-3 font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">Standort</span>
          <span className="col-span-2 font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">Seite</span>
          <span className="col-span-2 font-tech text-[10px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">Gerät / Browser</span>
        </div>
        {loading ? (
          <div className="px-6 py-16 text-center text-[var(--ci-grey)]"><Loader2 className="animate-spin inline mr-2" size={16} /> Lade Tracking-Daten…</div>
        ) : logs.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-[var(--ci-grey)]">Keine Besuche im gewählten Zeitraum.</div>
        ) : (
          logs.map((row, i) => (
            <div key={row.id || i} data-testid={`tracking-row-${i}`}
              className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 text-sm ${i < logs.length - 1 ? "border-b border-[#EEF1F4]" : ""} hover:bg-[#F7F9FA] transition-colors`}>
              <span className="md:col-span-3 font-tech text-xs text-[var(--ci-grey)]">{fmt(row.timestamp)}</span>
              <span className="md:col-span-2 font-tech text-xs text-[var(--ci-blue)]">{row.ip_address}</span>
              <span className="md:col-span-3 text-[var(--ci-grey)]">{row.location_str || "—"}</span>
              <span className="md:col-span-2 text-[var(--ci-grey)] truncate" title={row.path}>{row.path}</span>
              <span className="md:col-span-2 text-[var(--ci-grey)] text-xs">{parseUA(row.user_agent)}</span>
            </div>
          ))
        )}
      </div>
      <p className="mt-4 text-xs text-[var(--ci-grey)] leading-relaxed">
        IP-Adressen werden gemäß DSGVO sofort nach Auflösung des Standorts anonymisiert (letztes Oktett = 0). Es werden keine Cookies gesetzt und keine personenbezogenen Merkmale gespeichert.
      </p>
    </div>
  );
}
